import { isAxiosError } from 'axios';

import { useMutation, useQueryClient } from '@tanstack/react-query';

import { portalConnectionQueryKeys } from 'services/api/portalConnections';
import {
  getPortalCredentialPublicKey,
  portalCredentialQueryKeys,
  savePortalCredentials,
} from 'services/api/portalCredentials';
import { encryptCredentialEnvelope } from 'services/crypto/credentialEnvelope';

export interface SaveCredentialsVariables {
  sourceSystem: string;
  username: string;
  password: string;
}

/**
 * THE way a portal sign-in is saved: fetch the integration platform's public key, envelope-encrypt
 * the pair in this browser, post the ciphertext. Every surface that collects a password goes
 * through here so the plaintext set stays the browser and the headless worker — nothing else.
 *
 * Saving moves the Connected Portals row (status, savedAt, verifiedAt), so the connections cache
 * is invalidated on success: the row, not this component, is the source of truth for what a save
 * did, and a stale row is what makes a just-saved portal still read `No credentials`.
 */
export function useSavePortalCredentials({
  onSaved,
}: { onSaved?: (variables: SaveCredentialsVariables) => void } = {}) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ sourceSystem, username, password }: SaveCredentialsVariables) => {
      // Through the cache rather than a bare call: the key is immutable for the life of the
      // environment, so two saves in one session cost one KMS round trip.
      const key = await queryClient.fetchQuery({
        queryKey: portalCredentialQueryKeys.publicKey(),
        queryFn: getPortalCredentialPublicKey,
        staleTime: Infinity,
      });
      const credentialBlob = await encryptCredentialEnvelope({ username, password }, key.publicKey, key.keyId);
      return savePortalCredentials({ sourceSystem, username, credentialBlob });
    },
    onSuccess: (_result, variables) => {
      queryClient.invalidateQueries({ queryKey: portalConnectionQueryKeys.base });
      onSaved?.(variables);
    },
  });
}

/**
 * What went wrong, in words a coordinator can act on. The one expected 422 is the server's own
 * sentence (it names the remedy), so it is surfaced verbatim; everything else is deliberately
 * generic — a 502 from the key endpoint says nothing a user can use.
 */
export function credentialSaveErrorMessage(error: unknown): string {
  if (isAxiosError(error) && error.response?.status === 422) {
    // OliveBranch camelCases the error hash's keys on the way out, so the field key arrives as
    // sourceSystem.
    const serverMessage = error.response.data?.errors?.sourceSystem?.[0];
    return serverMessage ?? 'Olio could not save this sign-in.';
  }
  return 'Olio could not save this sign-in. Try again in a moment.';
}
